import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../../services/api'
import { useSocket } from '../../hooks/useSocket'

type ExceptionRow = {
  id: string
  type: string
  status: string
  notes?: string | null
  photo_url?: string | null
  group_code?: string | null
  group_label?: string | null
  created_by_name?: string | null
  created_by_role?: string | null
  resolution?: string | null
  created_at: string
}

const TYPE_LABEL: Record<string, string> = {
  missing_item: 'Missing item', extra_item: 'Extra item', damaged_item: 'Damaged item',
  wrong_service: 'Wrong service', lost_item: 'Lost item', customer_dispute: 'Customer dispute',
}

const statusColor = (s: string): { bg: string; fg: string } => {
  if (s === 'resolved' || s === 'closed') return { bg: '#EAF8EE', fg: '#15803D' }
  if (s === 'open') return { bg: '#FEF2F2', fg: '#B91C1C' }
  return { bg: '#FEF3C7', fg: '#92400E' } // in_review / assigned
}

const unwrap = (res: any) => res?.data ?? res

export default function OrderExceptionsSection({ orderId }: { orderId: string }) {
  const [rows, setRows] = useState<ExceptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('')
      const payload = unwrap(await adminApi.getOrderExceptions(orderId))
      setRows(Array.isArray(payload?.exceptions) ? payload.exceptions : [])
    } catch {
      setError('Unable to load exceptions.')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => { void load() }, [load])
  useSocket({ 'exception:created': (p: Record<string, unknown>) => {
    const ex = p?.exception as { order_id?: string } | undefined
    if (ex?.order_id === orderId) void load()
  } })

  const setStatus = async (id: string, status: string) => {
    if ((status === 'resolved' || status === 'closed') && !window.confirm(`Mark this exception as ${status}?`)) return
    setBusyId(id); setError('')
    try {
      await adminApi.updateException(id, { status })
      await load()
    } catch {
      setError('Could not update the exception.')
      await load()
    } finally {
      setBusyId(null)
    }
  }

  if (!loading && rows.length === 0) return null // nothing to show -> stay out of the way

  return (
    <div style={{ marginTop: 16, border: '1px solid #FCA5A5', borderRadius: 12, padding: 12, background: '#fff' }}>
      <h4 style={{ margin: '0 0 8px', color: '#B91C1C' }}>Exceptions ({rows.length})</h4>
      {error ? <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{error}</div> : null}
      {loading ? <div style={{ color: '#888', fontSize: 13 }}>Loading…</div> : null}

      {rows.map((r) => {
        const c = statusColor(r.status)
        const terminal = r.status === 'resolved' || r.status === 'closed'
        return (
          <div key={r.id} style={{ borderTop: '1px solid #F2EEE3', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontWeight: 600, color: '#0F2E2A' }}>
                {TYPE_LABEL[r.type] || r.type}
                {r.group_code ? <span style={{ marginLeft: 6, color: '#7C6F4F' }}>· {r.group_code} {r.group_label || ''}</span> : null}
              </div>
              <span style={{ background: c.bg, color: c.fg, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                {r.status.replace(/_/g, ' ')}
              </span>
            </div>
            {r.notes ? <div style={{ color: '#555', fontSize: 13, marginTop: 2 }}>{r.notes}</div> : null}
            <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>
              {r.created_by_name || r.created_by_role || 'staff'} · {new Date(r.created_at).toLocaleString()}
            </div>
            {r.photo_url ? <a href={r.photo_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1D4ED8' }}>View photo</a> : null}
            {!terminal ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {r.status === 'open' ? (
                  <button type="button" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'in_review')}
                    style={{ border: '1px solid #92400E', background: '#fff', color: '#92400E', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
                    Start review
                  </button>
                ) : null}
                <button type="button" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'resolved')}
                  style={{ border: 0, background: '#15803D', color: '#fff', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
                  {busyId === r.id ? '…' : 'Resolve'}
                </button>
                <button type="button" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'closed')}
                  style={{ border: '1px solid #E8E2D4', background: '#fff', color: '#555', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
                  Close
                </button>
              </div>
            ) : r.resolution ? (
              <div style={{ color: '#15803D', fontSize: 12, marginTop: 4 }}>Resolution: {r.resolution}</div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
